import { Controller } from '@hotwired/stimulus'
import Archiver from '../../lib/archiver'
import { randomId } from '../../utils'
import AlertController from './alert'
import AttachmentManagerItemController, { BusyChangeEvent } from './attachment-manager-item'
import BulkActionsController from './bulk-actions'
import DropzoneController, { AttachEvent } from './dropzone'

export default class AttachmentManagerController extends Controller<HTMLElement> {
  declare allowMultipleValue: boolean
  declare busyValue: boolean
  declare visibleItemsValue: number
  declare readonly attachmentManagerItemOutlets: AttachmentManagerItemController[]
  declare readonly bulkActionsOutlet: BulkActionsController
  declare readonly dropzoneOutlet: DropzoneController
  declare readonly alertOutlet: AlertController
  declare readonly hasDropzoneOutlet: boolean
  declare readonly hasItemOutlet: boolean
  declare readonly hasBulkActionsOutlet: boolean
  declare readonly hasAlertOutlet: boolean
  declare readonly itemTemplateTarget: HTMLTemplateElement
  declare readonly listTarget: HTMLUListElement
  cancelArchive: () => void | null

  static outlets = ['attachment-manager-item', 'bulk-actions', 'dropzone', 'alert']
  static targets = ['itemTemplate', 'list']
  static values = {
    allowMultiple: Boolean,
    busy: Boolean,
    visibleItems: Number,
  }

  connect() {
    this.#countVisibleItems()
    this.element.addEventListener('attachment-manager-item:detach', this.#countVisibleItems.bind(this))
    this.element.addEventListener('dropzone:attach', this.#handleAttachments.bind(this))
  }

  disconnect() {
    this.#handleCancelArchive()
    this.element.removeEventListener('attachment-manager-item:detach', this.#countVisibleItems.bind(this))
    this.element.removeEventListener('dropzone:attach', this.#handleAttachments.bind(this))
  }

  // Outlet callbacks

  attachmentManagerItemOutletConnected(item: AttachmentManagerItemController) {
    item.element.addEventListener('attachment-manager-item:busyChange', this.#handleItemBusyChange.bind(this))
    this.#countVisibleItems()
    this.hasBulkActionsOutlet && this.bulkActionsOutlet.checkboxChanged()
  }

  attachmentManagerItemOutletDisconnected(item: AttachmentManagerItemController) {
    item.element.addEventListener('attachment-manager-item:busyChange', this.#handleItemBusyChange.bind(this))
    this.#countVisibleItems()
    this.hasBulkActionsOutlet && this.bulkActionsOutlet.checkboxChanged()
  }

  // Value change callbacks

  visibleItemsValueChanged() {
    this.#handleVisibleItemsChange()
  }

  // Public methods

  bulkDetach() {
    this.#getCheckedItems().forEach(item => {
      item.detach()
    })
  }

  bulkDownload() {
    this.bulkActionsOutlet.toggleActionDisabledState('download-files', {
      busyOptions: {
        updateTooltipContent: 'Preparing files',
        busyType: 'indeterminate',
      },
    })

    try {
      const checkedItems = this.#getCheckedItems()
      const totalFileSize = checkedItems.reduce((totalSize, item) => totalSize + (item.fileValue.size || 0), 0)
      const checkedItemsData: Array<[string, string, number?]> = checkedItems.map(item => [
        item.blobValue.filename,
        item.fileNameTarget.href,
        item.fileValue.size,
      ])

      if (checkedItemsData.length === 1) {
        const url = checkedItemsData.at(0).at(1) as string
        // open the download url in a new tab
        window.open(url, '_blank')
        this.bulkActionsOutlet.toggleActionDisabledState('download-files')
      } else if (totalFileSize < 3 * 1024 ** 3) {
        // Total less than 3GB
        this.#performBundledDownload(checkedItemsData)
      } else {
        // When downloading large files, we will trigger all the download files separately
        if (
          !confirm(
            'Total file size is too large to bundle (>3GB). Your browser will download them separately. Make sure pop-ups are enabled for this site.',
          )
        ) {
          this.bulkActionsOutlet.toggleActionDisabledState('download-files')
          return
        }
        checkedItemsData.forEach(item => {
          window.open(item.at(1) as string, '_blank')
        })
        this.hasBulkActionsOutlet && this.bulkActionsOutlet.toggleActionDisabledState('download-files')
      }
    } catch (err) {
      console.error('AttachmentManaget: bulkDownload error', err)
      this.bulkActionsOutlet.toggleActionDisabledState('download-files')
      this.#handleCancelArchive()
    }
  }

  bulkReattach() {
    this.#getCheckedItems().forEach(item => {
      item.reattach()
    })
  }

  // Private methods

  #countVisibleItems() {
    this.visibleItemsValue = this.attachmentManagerItemOutlets.filter(item =>
      // TODO: Make this condition easier to understand. Here be dragons?
      item.detachedTypeValue === 'soft-delete' ? true : item.detachedValue !== true,
    ).length
    this.element.classList.toggle('empty', this.visibleItemsValue === 0)
  }

  #createItem(file: File) {
    const fragment = this.itemTemplateTarget.content.cloneNode(true) as DocumentFragment
    const item = fragment.querySelector('li')

    this.#setItemId(item)

    // NOTE: Because we're working with `File` objects—and Stimulus can't
    // really express `File` objects via its "Value" feature, we need to
    // do two things:
    //
    // 1. Create a new item and insert it into the DOM
    // 2. Find the newly-created item's outlet, and then
    //    pass the file into its `attach` method.
    //
    // If we were working with less complex data, we could just pass
    // it in via controller values and skip the second step.
    this.listTarget.appendChild(fragment)
    this.attachmentManagerItemOutlets.find(o => o.element === item).attach(file)
  }

  #getCheckedItems() {
    return this.attachmentManagerItemOutlets.filter(item =>
      this.bulkActionsOutlet.idsValue.includes(item.blobValue.signed_id),
    )
  }

  #handleAttachments(event: AttachEvent) {
    for (const file of Array.from(event.detail.files)) {
      this.#createItem(file)
    }
  }

  #handleBulkDownloadProgress({ currentStep, currentFileIndex, total, loaded, totalFileCount }) {
    if (currentStep !== 'Downloading Files' || !total) {
      return this.bulkActionsOutlet.toggleActionDisabledState('download-files', {
        forceValue: true,
        busyOptions: {
          updateTooltipContent: currentStep,
          busyType: 'indeterminate',
        },
      })
    }

    const progress = (loaded / total) * 100
    const tooltipContent = `Downloading files: ${currentFileIndex} of ${totalFileCount} (${progress.toFixed(1)}%)`
    this.bulkActionsOutlet.toggleActionDisabledState('download-files', {
      forceValue: true,
      busyOptions: {
        updateTooltipContent: tooltipContent,
        busyType: 'progress',
        progress,
      },
    })
  }

  #handleCancelArchive() {
    this.cancelArchive?.()
    this.cancelArchive = null
  }

  #handleItemBusyChange() {
    const busy = this.attachmentManagerItemOutlets.some(item => item.busyValue)

    this.busyValue = busy
    this.dispatch('busyChange', { detail: { busy } } satisfies Partial<BusyChangeEvent>)
  }

  #handleVisibleItemsChange() {
    let hideDropzone = false
    let hideList = false

    if (!this.allowMultipleValue) {
      if (this.visibleItemsValue > 0) {
        hideDropzone = true
      } else {
        hideList = true
      }
    }

    if (this.hasDropzoneOutlet) {
      this.dropzoneOutlet.element.classList.toggle('hidden', hideDropzone)
    }
    this.listTarget.classList.toggle('hidden', hideList)
  }

  #performBundledDownload(checkedItemsData) {
    const bundler = Archiver.init(checkedItemsData, this.#handleBulkDownloadProgress.bind(this))
    this.cancelArchive = () => bundler.cancel()
    bundler.bundleMe().finally(() => {
      this.hasBulkActionsOutlet && this.bulkActionsOutlet.toggleActionDisabledState('download-files')
    })
  }

  // When creating new items, make sure that their IDs are actually unique
  #setItemId(item: HTMLElement) {
    const itemId = randomId('attachment_manager_item')
    const progressBarId = `${itemId}_progress_bar`
    const progressBar = item.querySelector('[data-controller~="progress-bar"]')

    item.id = itemId
    item.dataset.attachmentManagerItemProgressBarOutlet = `#${progressBarId}`
    progressBar.id = progressBarId
  }
}
